import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SendIcon from "@mui/icons-material/Send";
import AddPropertyForm from "./AddPropertyForm";
import AddResidential from "./AddResidential";
import { get_my_general_enquiries, get_general_enquiry_details, submit_general_enquiry } from "../../../api/ControllApi";
import { useUser } from "../../../context/LoginDetail";
import { toast } from "react-toastify";

export default function GeneralEnquiry() {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState(0); // 0 = Submit Enquiry, 1 = My Enquiries
  const [formTab, setFormTab] = useState(0); // 0 = General Enquiry, 1 = Land, 2 = Flats/Villas
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  // General Enquiry Form State
  const [generalFormData, setGeneralFormData] = useState({
    full_name: "",
    phone_number: "",
    email_address: "",
    enquiry: "",
  });
  const [generalErrors, setGeneralErrors] = useState({});
  const [generalSubmitting, setGeneralSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setGeneralFormData((prev) => ({
        ...prev,
        full_name: prev.full_name || user.name || "",
        phone_number: prev.phone_number || user.phone || "",
        email_address: prev.email_address || user.email || "",
      }));
    }
  }, [user]);

  // Resubmission state
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [resubmitId, setResubmitId] = useState(null);
  const [resubmitType, setResubmitType] = useState(null); // 'land' or 'residential'
  const [resubmitData, setResubmitData] = useState(null);

  // Detail Modal state
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const res = await get_my_general_enquiries();
      if (res && res.data) {
        setEnquiries(res.data);
      }
    } catch (err) {
      console.error("Error fetching enquiries:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchEnquiries();
    }
  }, [activeTab]);

  const handleGeneralSubmit = async (e) => {
    if (e) e.preventDefault();
    const newErrors = {};
    if (!generalFormData.full_name.trim()) newErrors.full_name = "Full Name is required";
    if (!generalFormData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else if (generalFormData.phone_number.trim().length < 10) {
      newErrors.phone_number = "Please enter a valid 10-digit phone number";
    }
    if (!generalFormData.enquiry.trim()) {
      newErrors.enquiry = "Please enter your enquiry message";
    } else if (generalFormData.enquiry.trim().length < 5) {
      newErrors.enquiry = "Enquiry must be at least 5 characters long";
    }

    if (Object.keys(newErrors).length > 0) {
      setGeneralErrors(newErrors);
      return;
    }

    setGeneralSubmitting(true);
    try {
      const res = await submit_general_enquiry({
        full_name: generalFormData.full_name.trim(),
        phone_number: generalFormData.phone_number.trim(),
        email_address: generalFormData.email_address.trim() || undefined,
        enquiry: generalFormData.enquiry.trim(),
      });
      if (res?.status === 200 || res?.status === true) {
        toast.success(res?.message || "Enquiry submitted successfully! Our team will contact you soon.");
        setGeneralFormData((prev) => ({ ...prev, enquiry: "" }));
        setGeneralErrors({});
        setActiveTab(1);
        fetchEnquiries();
      } else {
        toast.error(res?.message || "Failed to submit enquiry. Please try again.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to submit enquiry.");
    } finally {
      setGeneralSubmitting(false);
    }
  };

  const handleEnquirySuccess = () => {
    // Reset resubmission state if active
    setIsResubmitting(false);
    setResubmitId(null);
    setResubmitType(null);
    setResubmitData(null);
    
    // Switch to list tab and reload
    setActiveTab(1);
    fetchEnquiries();
  };

  const handleViewDetails = async (item) => {
    setDetailsLoading(true);
    setModalOpen(true);
    try {
      const type = item.type_label.toLowerCase() === "land" ? "land" : "residential";
      const res = await get_general_enquiry_details(type, item.id);
      if (res && res.data) {
        setSelectedEnquiry({
          ...res.data,
          enquiry_type: type,
        });
      }
    } catch (err) {
      console.error("Error fetching details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleStartResubmit = (item) => {
    const type = item.type_label.toLowerCase() === "land" ? "land" : "residential";
    setDetailsLoading(true);
    get_general_enquiry_details(type, item.id)
      .then((res) => {
        if (res && res.data) {
          setResubmitId(item.id);
          setResubmitType(type);
          setResubmitData(res.data);
          setIsResubmitting(true);
          
          // Switch to first tab and set the appropriate sub-form tab
          setActiveTab(0);
          setFormTab(type === "land" ? 1 : 2);
        }
      })
      .catch((err) => {
        console.error("Error starting resubmit:", err);
      })
      .finally(() => {
        setDetailsLoading(false);
      });
  };

  const getStatusChip = (status) => {
    const s = status ? status.toLowerCase() : "pending";
    switch (s) {
      case "pending":
        return <Chip label="Pending" size="small" sx={{ bgcolor: "#fff3cd", color: "#856404", fontWeight: 600 }} />;
      case "under review":
        return <Chip label="Under Review" size="small" sx={{ bgcolor: "#d1ecf1", color: "#0c5460", fontWeight: 600 }} />;
      case "approved":
        return <Chip label="Approved" size="small" sx={{ bgcolor: "#d4edda", color: "#155724", fontWeight: 600 }} />;
      case "rejected":
        return <Chip label="Rejected" size="small" sx={{ bgcolor: "#f8d7da", color: "#721c24", fontWeight: 600 }} />;
      case "published":
        return <Chip label="Published" size="small" sx={{ bgcolor: "#cce5ff", color: "#004085", fontWeight: 600 }} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 4 }, bgcolor: "#fafafa", minHeight: "85vh" }}>
      {/* Top Banner Header */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #82A240 0%, #6A8A35 100%)",
          color: "#fff",
          p: 4,
          borderRadius: 4,
          mb: 4,
          boxShadow: "0 8px 24px rgba(130, 162, 64, 0.2)",
        }}
      >
        <Typography variant="h4" fontWeight={800} gutterBottom>
          General Property Enquiry
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Submit and manage land or residential property listings to the platform administrator.
        </Typography>
      </Paper>

      {/* Main Tabs Selection */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            "& .MuiTab-root": { fontWeight: 700, textTransform: "none", fontSize: "1.05rem" },
            "& .Mui-selected": { color: "#82A240 !important" },
            "& .MuiTabs-indicator": { bgcolor: "#82A240" },
          }}
        >
          <Tab label="Submit Property Enquiry" />
          <Tab label="My Enquiries List" />
        </Tabs>
      </Box>

      {/* SUBMIT ENQUIRY VIEW */}
      {activeTab === 0 && (
        <Box>
          {isResubmitting && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                bgcolor: "#fff3cd",
                borderLeft: "5px solid #ffc107",
                display: "flex",
                alignItems: "center",
                justifyContent: "between",
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700} color="#856404">
                  Resubmitting Enquiry #{resubmitId} ({resubmitType === "land" ? "Land" : "Flats/Villas"})
                </Typography>
                <Typography variant="body2" color="#856404">
                  You are editing your previously rejected submission. Update any fields and submit again to verify.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => {
                  setIsResubmitting(false);
                  setResubmitId(null);
                  setResubmitType(null);
                  setResubmitData(null);
                }}
                sx={{ ml: "auto", textTransform: "none" }}
              >
                Cancel Resubmission
              </Button>
            </Paper>
          )}

          {/* If NOT in resubmission mode OR we are in resubmitting land/residential specific mode, show sub-tabs */}
          {!isResubmitting && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              <Paper
                elevation={1}
                sx={{
                  display: "flex",
                  borderRadius: "9999px",
                  bgcolor: "#fff",
                  border: "1px solid #eee",
                  overflow: "hidden",
                  p: 0.5,
                }}
              >
                <Button
                  onClick={() => setFormTab(0)}
                  sx={{
                    borderRadius: "9999px",
                    px: { xs: 2, sm: 3.5 },
                    py: 1,
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: formTab === 0 ? "#82A240" : "transparent",
                    color: formTab === 0 ? "#fff" : "#555",
                    "&:hover": { bgcolor: formTab === 0 ? "#718F36" : "#f0f0f0" },
                  }}
                >
                  General Enquiry
                </Button>
                <Button
                  onClick={() => setFormTab(1)}
                  sx={{
                    borderRadius: "9999px",
                    px: { xs: 2, sm: 3.5 },
                    py: 1,
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: formTab === 1 ? "#82A240" : "transparent",
                    color: formTab === 1 ? "#fff" : "#555",
                    "&:hover": { bgcolor: formTab === 1 ? "#718F36" : "#f0f0f0" },
                  }}
                >
                  Land Property
                </Button>
                <Button
                  onClick={() => setFormTab(2)}
                  sx={{
                    borderRadius: "9999px",
                    px: { xs: 2, sm: 3.5 },
                    py: 1,
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: formTab === 2 ? "#82A240" : "transparent",
                    color: formTab === 2 ? "#fff" : "#555",
                    "&:hover": { bgcolor: formTab === 2 ? "#718F36" : "#f0f0f0" },
                  }}
                >
                  Flats / Villas
                </Button>
              </Paper>
            </Box>
          )}

          {/* 1. General Enquiry Form */}
          <Box sx={{ display: (formTab === 0 && !isResubmitting) ? "block" : "none" }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                border: "1px solid #E5E7EB",
                bgcolor: "#fff",
                maxWidth: 680,
                mx: "auto",
                boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
              }}
            >
              <Typography variant="h5" fontWeight={800} color="#163F1D" gutterBottom>
                Submit General Enquiry
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
                Have questions or need assistance? Fill out the form below and our team will get back to you shortly.
              </Typography>

              <Box component="form" onSubmit={handleGeneralSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <TextField
                  label="Full Name"
                  fullWidth
                  required
                  value={generalFormData.full_name}
                  onChange={(e) => {
                    setGeneralFormData((prev) => ({ ...prev, full_name: e.target.value }));
                    if (generalErrors.full_name) setGeneralErrors((prev) => ({ ...prev, full_name: "" }));
                  }}
                  error={Boolean(generalErrors.full_name)}
                  helperText={generalErrors.full_name}
                  placeholder="Enter your full name"
                  disabled={generalSubmitting}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />

                <TextField
                  label="Phone Number"
                  fullWidth
                  required
                  value={generalFormData.phone_number}
                  onChange={(e) => {
                    setGeneralFormData((prev) => ({ ...prev, phone_number: e.target.value }));
                    if (generalErrors.phone_number) setGeneralErrors((prev) => ({ ...prev, phone_number: "" }));
                  }}
                  error={Boolean(generalErrors.phone_number)}
                  helperText={generalErrors.phone_number}
                  placeholder="Enter your 10-digit phone number"
                  disabled={generalSubmitting}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />

                <TextField
                  label="Email Address (Optional)"
                  fullWidth
                  type="email"
                  value={generalFormData.email_address}
                  onChange={(e) => setGeneralFormData((prev) => ({ ...prev, email_address: e.target.value }))}
                  placeholder="Enter your email address"
                  disabled={generalSubmitting}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />

                <TextField
                  label="Your Enquiry / Message"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  value={generalFormData.enquiry}
                  onChange={(e) => {
                    setGeneralFormData((prev) => ({ ...prev, enquiry: e.target.value }));
                    if (generalErrors.enquiry) setGeneralErrors((prev) => ({ ...prev, enquiry: "" }));
                  }}
                  error={Boolean(generalErrors.enquiry)}
                  helperText={generalErrors.enquiry || "Minimum 5 characters"}
                  placeholder="Describe your inquiry or property requirement..."
                  disabled={generalSubmitting}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  disabled={generalSubmitting}
                  endIcon={!generalSubmitting && <SendIcon />}
                  sx={{
                    bgcolor: "#82A240",
                    color: "#fff",
                    fontWeight: 700,
                    py: 1.3,
                    borderRadius: 2.5,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    mt: 1,
                    "&:hover": { bgcolor: "#718F36" },
                  }}
                >
                  {generalSubmitting ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                      <span>Submitting...</span>
                    </Box>
                  ) : (
                    "Submit Enquiry"
                  )}
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* 2. Land Property Form Container */}
          <Box sx={{ display: (formTab === 1 && (!isResubmitting || resubmitType === "land")) ? "block" : "none" }}>
            <AddPropertyForm
              isEnquiryMode={true}
              onSubmitSuccess={handleEnquirySuccess}
              isResubmit={isResubmitting && resubmitType === "land"}
              resubmitId={resubmitId}
              existingData={resubmitData}
            />
          </Box>

          {/* 3. Flats / Villas Form Container */}
          <Box sx={{ display: (formTab === 2 && (!isResubmitting || resubmitType === "residential")) ? "block" : "none" }}>
            <AddResidential
              isEnquiryMode={true}
              onSubmitSuccess={handleEnquirySuccess}
              isResubmit={isResubmitting && resubmitType === "residential"}
              resubmitId={resubmitId}
              existingData={resubmitData}
            />
          </Box>
        </Box>
      )}

      {/* MY ENQUIRIES LIST VIEW */}
      {activeTab === 1 && (
        <TableContainer component={Paper} sx={{ borderRadius: 3, border: "1px solid #eef", boxShadow: "0 4px 12px rgba(0,0,0,0.02)" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#82A240" }} />
            </Box>
          ) : enquiries.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <AssignmentIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Enquiries Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                You haven't submitted any general property enquiries yet.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Enquiry ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Property Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Property Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Listing Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Submitted Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Current Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enquiries.map((item) => (
                  <TableRow key={`${item.type_label}-${item.id}`} hover>
                    <TableCell>#{item.id}</TableCell>
                    <TableCell fontWeight={600}>{item.property_name || "N/A"}</TableCell>
                    <TableCell>{item.property_type_name}</TableCell>
                    <TableCell>{item.listing_type_label}</TableCell>
                    <TableCell>{item.submitted_date}</TableCell>
                    <TableCell>{getStatusChip(item.status)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => handleViewDetails(item)}
                        title="View Details"
                        sx={{ color: "#82A240" }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {item.status?.toLowerCase() === "rejected" && (
                        <IconButton
                          color="warning"
                          onClick={() => handleStartResubmit(item)}
                          title="Edit & Resubmit"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      {/* VIEW DETAILS DIALOG */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700 }}>
          Enquiry Details #{selectedEnquiry?.id}
          <IconButton onClick={() => setModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#82A240" }} />
            </Box>
          ) : selectedEnquiry ? (
            <Grid container spacing={3}>
              {selectedEnquiry.rejection_reason && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: "#f8d7da", borderLeft: "5px solid #dc3545", color: "#721c24" }}>
                    <Typography variant="subtitle2" fontWeight={700}>Rejection Reason:</Typography>
                    <Typography variant="body2">{selectedEnquiry.rejection_reason}</Typography>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Property Name</Typography>
                <Typography variant="body1" fontWeight={700} gutterBottom>
                  {selectedEnquiry.property_name || selectedEnquiry.name || "N/A"}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Listing Category</Typography>
                <Typography variant="body1" gutterBottom>{selectedEnquiry.listing_type_label || "N/A"}</Typography>

                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Area (sqft)</Typography>
                <Typography variant="body1" gutterBottom>{selectedEnquiry.area || selectedEnquiry.area_sqft || "N/A"}</Typography>

                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Price</Typography>
                <Typography variant="body1" gutterBottom>₹{selectedEnquiry.price || "N/A"}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                <Typography variant="body1" gutterBottom>{selectedEnquiry.address || selectedEnquiry.location || "N/A"}</Typography>

                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>ULPIN Number / ID</Typography>
                <Typography variant="body1" gutterBottom>{selectedEnquiry.uld_no || "N/A"}</Typography>

                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Status</Typography>
                <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedEnquiry.status)}</Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>Description</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-line", bgcolor: "#f8f9fa", p: 2, borderRadius: 2, mt: 0.5 }}>
                  {selectedEnquiry.description || selectedEnquiry.about_property || "N/A"}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography>Failed to load enquiry details.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {selectedEnquiry?.status?.toLowerCase() === "rejected" && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                setModalOpen(false);
                handleStartResubmit(selectedEnquiry);
              }}
              sx={{ bgcolor: "#82A240", "&:hover": { bgcolor: "#718F36" } }}
            >
              Correct & Resubmit
            </Button>
          )}
          <Button onClick={() => setModalOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
